import { growthBook } from "@/helpers/growthbook";

export const IS_CART_OPEN = "cart_isOpen";

/**
 * Returns the effective subscription discount percent for **pricing** (cart, PDP
 * line prices). When the Shopify control percent is 25%, may swap to the mapped
 * percent for the active `price-discount-test` arm if `variationDiscounts` is set.
 * Marketing copy that should always read “15%” for the -15 plan uses
 * `variantSelect.subscriptionDiscountForDisplay` instead.
 */
export function getEffectiveSubscriptionDiscountPercent(
  percent: number,
  options?: { isBulk?: boolean; isBundle?: boolean },
  variationDiscounts?: Partial<Record<number, number>>
): number {
  if (options?.isBulk || options?.isBundle) {
    return percent;
  }

  if (percent === 25) {
    try {
      const raw = growthBook.getFeatureValue("price-discount-test", 0);
      const variation = Number(raw);
      if (
        variationDiscounts &&
        typeof variationDiscounts[variation] === "number"
      ) {
        return variationDiscounts[variation] as number;
      }
    } catch (_e) {
      // Fail open to control value if GrowthBook isn't available in this runtime
    }
  }

  return percent;
}

/**
 * Parses the variant-level `custom.product_spot_discount` metafield value into
 * fixed USD off per unit. Accepts:
 *   - number / numeric string (e.g. `5`, `"5.00"`)
 *   - `{ discount: 5 | "5.00" }` (already-parsed metaobject)
 *   - already-set numeric values from upstream parsers
 *
 * Returns `0` when missing, invalid, a Shopify GID, or non-positive.
 */
export function parseVariantSpotDiscountDollars(raw: unknown): number {
  if (raw == null || raw === "") return 0;

  let v: unknown = raw;
  if (typeof v === "object" && v !== null && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    if ("discount" in o && o.discount != null && o.discount !== "") {
      v = o.discount;
    }
  }

  if (typeof v === "string" && v.startsWith("gid://")) return 0;

  const n =
    typeof v === "string"
      ? parseFloat(v.trim())
      : typeof v === "number"
        ? v
        : Number(v);

  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/**
 * One-time spot discount in **fixed USD** off per unit, gated by:
 *   - `price-discount-test` GrowthBook variation `1`
 *   - product is not Bulk / Bundle
 *
 * The discount is sourced from the **variant** metafield
 * `custom.product_spot_discount` (metaobject reference → `discount` decimal).
 */
export function getEffectiveOnetimeSpotDiscountDollars(
  variant:
    | {
        productSpotDiscount?: number | string;
      }
    | undefined,
  product:
    | {
        type?: string;
        bundledItems?: unknown[];
        tags?: string[];
        title?: string;
      }
    | undefined,
  options?: { isBulk?: boolean; isBundle?: boolean }
): number {
  if (!variant) return 0;
  if (options?.isBulk || options?.isBundle) return 0;
  if (
    product?.type === "Bundle" ||
    (product?.bundledItems && product.bundledItems.length > 0) ||
    product?.tags?.includes("bundle")
  ) {
    return 0;
  }
  if (product?.title?.toLowerCase().includes("bulk")) return 0;

  const dollars = parseVariantSpotDiscountDollars(variant.productSpotDiscount);
  if (dollars <= 0) return 0;

  try {
    const raw = growthBook.getFeatureValue("price-discount-test", 0);
    const variation = Number(String(raw).trim());
    if (!Number.isFinite(variation) || variation !== 1) return 0;
  } catch {
    return 0;
  }

  return dollars;
}

/**
 * Gets the free shipping bundle product IDs from the global metafield.
 * Returns empty array if metafield is empty or not set (no free shipping).
 *
 * @param metafields - Optional array of global metafields
 * @returns Array of product IDs for free shipping bundle, or empty array if no free shipping
 */
export function getFreeShippingBundleProductIds(metafields?: any[]): number[] {
  // If no metafields provided, return empty array (no free shipping)
  if (!metafields || !Array.isArray(metafields)) {
    return [];
  }

  // Filter out null/undefined values and find the target metafield
  const validMetafields = metafields.filter((mf) => mf != null);
  const metafield = validMetafields.find(
    (mf) =>
      mf.namespace === "custom" && mf.key === "free_shipping_bundle_product_ids"
  );

  // If metafield not found or value is empty, return empty array (no free shipping)
  if (!metafield?.value || metafield.value.trim() === "") {
    return [];
  }

  try {
    const parsedValue = JSON.parse(metafield.value);

    // If parsed value is not an array or is empty, return empty array
    if (!Array.isArray(parsedValue) || parsedValue.length === 0) {
      return [];
    }

    return parsedValue;
  } catch (error) {
    console.error("Failed to parse free shipping bundle product IDs:", error);
    // If parsing fails, return empty array (no free shipping)
    return [];
  }
}
